import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Download, ArrowLeft, Image as ImageIcon, Layers, ChevronDown, ChevronLeft, ChevronRight, GripVertical, Stamp, Trash2, Upload, Move, Eye, MessageSquarePlus, Pencil, X, Wand2, Sparkles } from 'lucide-react';
import { WatermarkSettings, GenerationMode, VectorFloorPlan, TextLabel, BrandingProfile, DEFAULT_BRANDING_PROFILE } from '../types';
import Viewer360 from './Viewer360';
import FloorPlanEditor, { FloorPlanData } from './FloorPlanEditor';
import EditorRightSidebar, { SelectorTool, AmendmentAction } from './EditorRightSidebar';
import ImageCanvas, { SelectionBox } from './ImageCanvas';
import { executeCanvasAmendment, AmendmentType } from '../services/canvasDrawing';
import { inpaintWithReplicate } from '../services/replicate';

function watermarkPositionToCoords(
  position: BrandingProfile['watermarkPosition'],
  scale: number
): { x: number; y: number } {
  const margin = 0.02;
  switch (position) {
    case 'top-left':     return { x: margin, y: margin };
    case 'top-right':    return { x: 1 - scale - margin, y: margin };
    case 'bottom-left':  return { x: margin, y: 0.85 };
    case 'bottom-right': return { x: 1 - scale - margin, y: 0.85 };
    case 'center':       return { x: 0.5 - scale / 2, y: 0.45 };
    default:             return { x: 1 - scale - margin, y: 0.85 };
  }
}

interface ResultViewerProps {
  originalUrl: string;
  processedUrl: string;
  onBack: () => void;
  onDownload: (format: 'png' | 'jpeg', watermark?: WatermarkSettings | null) => void;
  onAccept: () => void;
  onRequestAmendment?: (amendmentText: string) => void;
  mode?: GenerationMode;
  vectorData?: VectorFloorPlan | null;
  // Credit system props
  userCredits?: number;
  downloadCreditCost?: number;
  onConsumeCredits?: (amount: number) => Promise<boolean>;
  onOrderPro?: (sketchUrl: string) => void;
  isLoggedIn?: boolean;
  /** Agency branding profile – pre-populates watermark when logo is set */
  brandingProfile?: BrandingProfile;
  /** Open the branding configuration modal */
  onOpenBranding?: () => void;
}

const ResultViewer: React.FC<ResultViewerProps> = ({ 
  originalUrl, 
  processedUrl, 
  onBack, 
  onDownload,
  onAccept,
  onRequestAmendment,
  mode = 'visual',
  vectorData,
  userCredits = 0,
  downloadCreditCost = 2,
  onConsumeCredits,
  onOrderPro,
  isLoggedIn = false,
  brandingProfile = DEFAULT_BRANDING_PROFILE,
  onOpenBranding,
}) => {
  const [viewMode, setViewMode] = useState<'compare' | 'processed' | 'original'>('compare');
  const [sliderPosition, setSliderPosition] = useState(50);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [amendmentText, setAmendmentText] = useState('');
  const [showAmendPanel, setShowAmendPanel] = useState(false);
  const [isSubmittingAmendment, setIsSubmittingAmendment] = useState(false);

  const AMENDMENT_SUGGESTIONS = [
    'Add a door to the master bedroom',
    'Remove the wall between kitchen and dining',
    'Add an en-suite bathroom',
    'Add a window to the study',
    'Swap the kitchen and living room',
    'Make the living room larger',
    'Add a utility room',
    'Relabel room names',
  ];
  
  // AI Editor Sidebar State
  const [showAiSidebar, setShowAiSidebar] = useState(false);
  const [aiTool, setAiTool] = useState<SelectorTool>('box');
  const [aiSelection, setAiSelection] = useState<SelectionBox | null>(null);
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  
  // Text Labels State (overlay-based, not canvas)
  const [textLabels, setTextLabels] = useState<TextLabel[]>([]);
  
  // Watermark State
  const [isWatermarking, setIsWatermarking] = useState(false);
  const [watermark, setWatermark] = useState<WatermarkSettings | null>(() => {
    // Pre-populate from branding profile if logo + watermark enabled
    if (brandingProfile.watermarkEnabled && brandingProfile.logoDataUrl) {
      const coords = watermarkPositionToCoords(brandingProfile.watermarkPosition, brandingProfile.watermarkScale);
      return {
        imageUrl: brandingProfile.logoDataUrl,
        x: coords.x,
        y: coords.y,
        scale: brandingProfile.watermarkScale,
        opacity: brandingProfile.watermarkOpacity,
      };
    }
    return null;
  });
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  
  // 360 Viewer State
  const [is360Image, setIs360Image] = useState(false);
  const [show360Viewer, setShow360Viewer] = useState(false);
  const [viewer360Mode, setViewer360Mode] = useState<'original' | 'processed'>('processed');

  // Floor Plan Editor State
  // In vector mode, start in editing mode by default
  const [isEditing, setIsEditing] = useState(mode === 'vector');
  const [editorImageUrl, setEditorImageUrl] = useState(processedUrl);
  const [measurements, setMeasurements] = useState<FloorPlanData['measurements']>([]);
  
  // Sync watermark from branding profile when it changes
  useEffect(() => {
    if (brandingProfile.watermarkEnabled && brandingProfile.logoDataUrl) {
      const coords = watermarkPositionToCoords(brandingProfile.watermarkPosition, brandingProfile.watermarkScale);
      setWatermark({
        imageUrl: brandingProfile.logoDataUrl,
        x: coords.x,
        y: coords.y,
        scale: brandingProfile.watermarkScale,
        opacity: brandingProfile.watermarkOpacity,
      });
    }
  }, [brandingProfile.watermarkEnabled, brandingProfile.logoDataUrl, brandingProfile.watermarkPosition, brandingProfile.watermarkScale, brandingProfile.watermarkOpacity]);

  // Ensure editing mode is enabled in vector mode (handles late mode changes)
  useEffect(() => {
    if (mode === 'vector' && !isEditing) {
      setIsEditing(true);
    }
  }, [mode]);
  
  // Enhanced Look (beta) - styling layer for vector mode
  // When enabled, applies enhanced styling (fills, shadows, gradients) without changing geometry
  const [enhancedLook, setEnhancedLook] = useState(false);
  
  // Convert vector data to measurements format for the editor
  useEffect(() => {
    if (mode === 'vector' && vectorData) {
      const convertedMeasurements: FloorPlanData['measurements'] = [
        // Convert labels
        ...vectorData.labels.map(label => ({
          id: label.id,
          type: 'label' as const,
          x: label.x,
          y: label.y,
          text: label.text,
          fontSize: label.fontSize,
        })),
        // Convert walls to lines
        ...vectorData.walls.map(wall => ({
          id: wall.id,
          type: 'line' as const,
          x: wall.x1,
          y: wall.y1,
          x2: wall.x2,
          y2: wall.y2,
        })),
      ];
      setMeasurements(convertedMeasurements);
    }
  }, [mode, vectorData]);

  const containerRef = useRef<HTMLDivElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const processedImageRef = useRef<HTMLImageElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Direct download function (local implementation as backup)
  const handleDirectDownload = (format: 'png' | 'jpeg') => {
    console.log('handleDirectDownload called:', format);
    
    if (!processedUrl) {
      console.error('No processedUrl available');
      alert('No image available to download');
      return;
    }

    try {
      // Try calling the passed onDownload prop first
      if (onDownload) {
        console.log('Calling onDownload prop...');
        onDownload(format, watermark);
      } else {
        // Fallback: direct download
        console.log('onDownload prop not available, using fallback');
        const link = document.createElement('a');
        link.href = processedUrl;
        link.download = `Sketchplan-${Date.now()}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download error:', error);
      // Ultimate fallback - open in new tab
      window.open(processedUrl, '_blank');
    }
    
    setShowDownloadMenu(false);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderPosition(parseInt(e.target.value));
  };

  // AI Editor Sidebar Handlers
  const [pendingAiPrompt, setPendingAiPrompt] = useState<string | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(processedUrl);
  const [aiError, setAiError] = useState<string | null>(null);

  // Undo/Redo History
  const [imageHistory, setImageHistory] = useState<string[]>([processedUrl]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Get Supabase config for Edge Function calls
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || '';
  const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || '';

  /**
   * Update the working image URL - keeps both currentImageUrl and editorImageUrl in sync
   * Also pushes to history for undo/redo
   */
  const updateWorkingImage = useCallback((newUrl: string) => {
    setCurrentImageUrl(newUrl);
    setEditorImageUrl(newUrl);
    
    // Push to history (truncate any redo history)
    setImageHistory(prev => [...prev.slice(0, historyIndex + 1), newUrl]);
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  /**
   * Undo the last image change
   */
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const prevUrl = imageHistory[newIndex];
      setCurrentImageUrl(prevUrl);
      setEditorImageUrl(prevUrl);
    }
  }, [historyIndex, imageHistory]);

  /**
   * Redo a previously undone change
   */
  const handleRedo = useCallback(() => {
    if (historyIndex < imageHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const nextUrl = imageHistory[newIndex];
      setCurrentImageUrl(nextUrl);
      setEditorImageUrl(nextUrl);
    }
  }, [historyIndex, imageHistory]);

  // Computed undo/redo state
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < imageHistory.length - 1;

  // Execute AI inpainting using Replicate SDXL
  const executeAiInpaint = useCallback(async (prompt: string, selection: SelectionBox | null, actionType?: string) => {
    if (!selection) {
      setAiError('Please select an area first');
      return;
    }

    setIsAiProcessing(true);
    setAiError(null);
    
    console.log('='.repeat(60));
    console.log('EXECUTING REPLICATE INPAINT');
    console.log('Action Type:', actionType);
    console.log('Prompt:', prompt);
    console.log('Selection:', selection);
    console.log('Image URL:', currentImageUrl);
    console.log('='.repeat(60));

    try {
      // Use Replicate SDXL for precise inpainting
      const result = await inpaintWithReplicate(
        currentImageUrl,
        {
          x: selection.x,
          y: selection.y,
          width: selection.width,
          height: selection.height,
        },
        prompt,
        supabaseUrl,
        supabaseAnonKey
      );
      
      if (!result.success || !result.imageUrl) {
        throw new Error(result.error || 'Inpainting failed');
      }
      
      // Update the image with the AI result
      updateWorkingImage(result.imageUrl);
      setAiSelection(null);
      setPendingAiPrompt(null);
      
      console.log('Replicate Inpaint successful - image updated');
      console.log('Prediction ID:', result.predictionId);
    } catch (error: any) {
      console.error('Replicate Inpaint failed:', error);
      setAiError(error.message || 'AI processing failed. Please try again.');
    } finally {
      setIsAiProcessing(false);
    }
  }, [currentImageUrl, supabaseUrl, supabaseAnonKey, updateWorkingImage]);

  const handleAiAmendmentClick = useCallback(async (action: AmendmentAction) => {
    if (!aiSelection) {
      console.log('No selection - cannot apply amendment');
      setAiError('Please select an area first');
      return;
    }
    
    console.log('='.repeat(60));
    console.log('CANVAS AMENDMENT ACTION:', action.type);
    console.log('Selection:', {
      x: (aiSelection.x * 100).toFixed(1) + '%',
      y: (aiSelection.y * 100).toFixed(1) + '%',
      width: (aiSelection.width * 100).toFixed(1) + '%',
      height: (aiSelection.height * 100).toFixed(1) + '%',
    });
    console.log('='.repeat(60));
    
    setIsAiProcessing(true);
    setAiError(null);

    try {
      // Use canvas-based drawing (instant, free, reliable)
      const result = await executeCanvasAmendment(
        currentImageUrl,
        {
          x: aiSelection.x,
          y: aiSelection.y,
          width: aiSelection.width,
          height: aiSelection.height,
        },
        action.type as AmendmentType
      );

      if (!result.success) {
        throw new Error(result.error || 'Drawing failed');
      }

      // Update the image
      updateWorkingImage(result.imageDataUrl);
      setAiSelection(null);
      
      console.log('Canvas amendment successful');
    } catch (error: any) {
      console.error('Canvas amendment failed:', error);
      setAiError(error.message || 'Amendment failed');
    } finally {
      setIsAiProcessing(false);
    }
  }, [aiSelection, currentImageUrl, updateWorkingImage]);

  // Handle adding text labels (overlay-based, not canvas)
  const handleAddText = useCallback((text: string, fontSize: number) => {
    if (!aiSelection) {
      console.log('No selection - cannot add text');
      setAiError('Please select an area first');
      return;
    }
    
    console.log('='.repeat(60));
    console.log('ADD TEXT OVERLAY:', text);
    console.log('Font Size:', fontSize);
    console.log('Selection:', aiSelection);
    console.log('='.repeat(60));
    
    // Create new text label at the center of the selection
    const newLabel: TextLabel = {
      id: `label-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      text: text,
      x: aiSelection.x + aiSelection.width / 2,
      y: aiSelection.y + aiSelection.height / 2,
      fontSize: fontSize,
      color: '#000000',
      isEditing: false,
    };
    
    setTextLabels(prev => [...prev, newLabel]);
    setAiSelection(null);
    
    console.log('Text label added:', newLabel);
  }, [aiSelection]);

  // Handle text labels change from overlay
  const handleTextLabelsChange = useCallback((labels: TextLabel[]) => {
    setTextLabels(labels);
  }, []);

  // This is kept for backwards compatibility but not used for text anymore
  const handleAddTextLegacy = useCallback(async (text: string, fontSize: number) => {
    // Legacy canvas-based text - no longer used
    console.log('Legacy text function called - using overlay instead');
    handleAddText(text, fontSize);
  }, [handleAddText]);

  const handleAiCustomPrompt = useCallback((prompt: string) => {
    console.log('='.repeat(60));
    console.log('AI CUSTOM PROMPT:', prompt);
    if (aiSelection) {
      console.log('With selection:', {
        x: (aiSelection.x * 100).toFixed(1) + '%',
        y: (aiSelection.y * 100).toFixed(1) + '%',
        width: (aiSelection.width * 100).toFixed(1) + '%',
        height: (aiSelection.height * 100).toFixed(1) + '%',
      });
    } else {
      console.log('No selection - applying to full image');
    }
    console.log('='.repeat(60));
    
    // Store the prompt for "Update Plan" button
    setPendingAiPrompt(prompt);
  }, [aiSelection]);

  const handleAiUpdatePlan = useCallback(() => {
    const prompt = pendingAiPrompt || 'Enhance and clean up this floor plan';
    
    console.log('='.repeat(60));
    console.log('AI UPDATE PLAN TRIGGERED');
    console.log('Prompt:', prompt);
    console.log('Has selection:', aiSelection !== null);
    console.log('='.repeat(60));
    
    // Execute real AI inpainting
    executeAiInpaint(prompt, aiSelection);
  }, [pendingAiPrompt, aiSelection, executeAiInpaint]);

  const handleAiDownload = useCallback(() => {
    handleDirectDownload('png');
  }, []);

  // Keyboard navigation for slider
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (viewMode === 'compare') {
        if (e.key === 'ArrowLeft') {
          setSliderPosition(prev => Math.max(0, prev - 5));
        } else if (e.key === 'ArrowRight') {
          setSliderPosition(prev => Math.min(100, prev + 5));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode]);

  // Detect if image is 360 equirectangular
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      const is360 = Math.abs(ratio - 2.0) < 0.1;
      setIs360Image(is360);
    };
    img.src = processedUrl;
  }, [processedUrl]);

  // Click outside to close download menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Watermark Logic ---
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const url = URL.createObjectURL(file);
      setWatermark({
        imageUrl: url,
        x: 0.5, // Center
        y: 0.5,
        scale: 0.2, // 20% width
        opacity: 0.8
      });
      setViewMode('processed'); // Switch to view where watermark is visible
    }
  };

  const startDrag = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDraggingLogo(true);
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!isDraggingLogo || !processedImageRef.current || !watermark) return;

      const rect = processedImageRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;

      // Calculate relative position (0-1)
      let x = (clientX - rect.left) / rect.width;
      let y = (clientY - rect.top) / rect.height;

      // Clamp
      x = Math.max(0, Math.min(1, x));
      y = Math.max(0, Math.min(1, y));

      setWatermark(prev => prev ? { ...prev, x, y } : null);
    };

    const handleUp = () => {
      setIsDraggingLogo(false);
    };

    if (isDraggingLogo) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('mouseup', handleUp);
      window.addEventListener('touchend', handleUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
    };
  }, [isDraggingLogo, watermark]);

  // --- Floor Plan Editor Logic ---
  const handleEditorSave = (data: FloorPlanData, imageBlob: Blob | null) => {
    // Save the measurements
    setMeasurements(data.measurements);
    
    // If we got an image blob, create a URL for it to replace the processed image
    if (imageBlob) {
      const newImageUrl = URL.createObjectURL(imageBlob);
      setEditorImageUrl(newImageUrl);
    }
    
    // Exit editing mode
    setIsEditing(false);
    
    console.log('Floor plan saved:', data);
  };

  // Keep working image URL in sync when processedUrl changes
  // This resets the history when a new image is loaded
  useEffect(() => {
    setCurrentImageUrl(processedUrl);
    setEditorImageUrl(processedUrl);
    setImageHistory([processedUrl]);
    setHistoryIndex(0);
  }, [processedUrl]);

  return (
    <div className="flex flex-col h-full bg-black animate-fade-in">
       {/* Toolbar - Desktop */}
       <div className="hidden sm:flex h-16 bg-slate-900 border-b border-slate-800 items-center justify-between px-6 shrink-0 shadow-sm z-40 relative">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white font-medium transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Editor
          </button>

          <div className="flex bg-slate-800 p-1 rounded-lg">
             <button
               onClick={() => setViewMode('original')}
               className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === 'original' ? 'bg-slate-700 shadow text-white' : 'text-slate-400 hover:text-slate-200'}`}
             >
               Original
             </button>
             <button
               onClick={() => setViewMode('compare')}
               className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === 'compare' ? 'bg-slate-700 shadow text-white' : 'text-slate-400 hover:text-slate-200'}`}
             >
               Before / After
             </button>
             <button
               onClick={() => setViewMode('processed')}
               className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === 'processed' ? 'bg-slate-700 shadow text-white' : 'text-slate-400 hover:text-slate-200'}`}
             >
               Result
             </button>
          </div>

          <div className="flex items-center gap-3">
            {is360Image && (
              <button
                onClick={() => { setViewer360Mode('processed'); setShow360Viewer(true); }}
                className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all bg-blue-600 hover:bg-blue-500 text-white border border-blue-400/30"
              >
                <Eye size={18} />
                View 360°
              </button>
            )}
            
            <button
              onClick={() => setIsWatermarking(!isWatermarking)}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${isWatermarking ? 'bg-teal-900/30 text-teal-500 border border-teal-800' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'}`}
            >
              <Stamp size={18} />
              Watermark
            </button>

            {/* Branding button */}
            {onOpenBranding && (
              <button
                onClick={onOpenBranding}
                className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all bg-slate-800 text-slate-300 border border-slate-700 hover:bg-purple-900/40 hover:text-purple-300 hover:border-purple-700"
              >
                {brandingProfile.logoDataUrl
                  ? <img src={brandingProfile.logoDataUrl} alt="" className="h-4 w-auto object-contain" />
                  : <Sparkles size={18} />
                }
                <span className="hidden lg:inline">Branding</span>
                {(brandingProfile.primaryColor !== DEFAULT_BRANDING_PROFILE.primaryColor || brandingProfile.agencyName) && (
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: brandingProfile.primaryColor }}
                  />
                )}
              </button>
            )}

            {/* Edit Floor Plan - Only show for vector/CAD mode */}
            {mode === 'vector' && (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all bg-amber-600 hover:bg-amber-500 text-white shadow-md hover:shadow-lg"
                  title="Edit floor plan measurements and labels"
                >
                  <Pencil size={18} />
                  Edit Floor Plan
                </button>
                
                {/* Enhanced Look Toggle (beta) */}
                <button
                  onClick={() => setEnhancedLook(!enhancedLook)}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                    enhancedLook 
                      ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-md' 
                      : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'
                  }`}
                  title="Apply enhanced visual styling (styling only, geometry unchanged)"
                >
                  <Sparkles size={18} />
                  <span className="hidden lg:inline">Enhanced look</span>
                  <span className="text-[10px] bg-purple-800/50 text-purple-300 px-1.5 py-0.5 rounded">beta</span>
                </button>
              </>
            )}

            {/* Amend Plan - Show when amendment handler is available */}
            {onRequestAmendment && (
              <button
                onClick={() => { setShowAmendPanel(!showAmendPanel); setShowAiSidebar(false); }}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${showAmendPanel ? 'bg-orange-600 text-white shadow-md' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-orange-900/40 hover:text-orange-300 hover:border-orange-700'}`}
                title="Request AI amendments to this floor plan"
              >
                <MessageSquarePlus size={18} />
                Amend Plan
              </button>
            )}

            {/* AI Editor Toggle - Show for visual/AI mode */}
            {mode === 'visual' && (
              <button
                onClick={() => { setShowAiSidebar(!showAiSidebar); setShowAmendPanel(false); }}
                className={`px-5 py-2 rounded-lg font-medium flex items-center gap-2 transition-all shadow-md ${showAiSidebar ? 'bg-teal-600 text-white' : 'bg-slate-700 hover:bg-slate-600 text-white hover:shadow-lg'}`}
                title="Open AI editing tools"
              >
                <Wand2 size={18} />
                {showAiSidebar ? 'Close Editor' : 'AI Editor'}
              </button>
            )}

            <div className="relative" ref={downloadMenuRef}>
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className="px-5 py-2 rounded-lg font-medium flex items-center gap-2 transition-all shadow-md bg-slate-700 hover:bg-slate-600 text-white hover:shadow-lg"
              >
                <Download size={18} />
                Download
                <ChevronDown size={14} className={`transition-transform duration-200 ${showDownloadMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showDownloadMenu && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-slate-900 rounded-lg shadow-xl border border-slate-700 py-2 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right">
                   {/* Standard Downloads */}
                   <div className="px-4 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Download Format
                   </div>
                   <button 
                     type="button"
                     onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('jpeg'); }}
                     className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors flex items-center justify-between group cursor-pointer"
                   >
                     <span>JPEG Image</span>
                     <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded border border-slate-700 group-hover:bg-slate-700">Smaller</span>
                   </button>
                   <button 
                     type="button"
                     onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('png'); }}
                     className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors flex items-center justify-between group cursor-pointer"
                   >
                     <span>PNG Image</span>
                     <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded border border-slate-700 group-hover:bg-slate-700">Lossless</span>
                   </button>

                </div>
              )}
            </div>
          </div>
       </div>

       {/* Toolbar - Mobile */}
       <div className="sm:hidden bg-slate-900 border-b border-slate-800 shrink-0 shadow-sm z-40">
          {/* Top row: Back + View Mode */}
          <div className="flex items-center justify-between px-3 py-2">
            <button 
              onClick={onBack}
              className="flex items-center gap-1.5 text-slate-400 hover:text-white text-sm font-medium transition-colors"
            >
              <ArrowLeft size={16} />
              <span>Back</span>
            </button>

            <div className="flex bg-slate-800 p-0.5 rounded-lg">
               <button
                 onClick={() => setViewMode('original')}
                 className={`px-2.5 py-1 text-xs font-medium rounded transition-all ${viewMode === 'original' ? 'bg-slate-700 shadow text-white' : 'text-slate-400'}`}
               >
                 Original
               </button>
               <button
                 onClick={() => setViewMode('compare')}
                 className={`px-2.5 py-1 text-xs font-medium rounded transition-all ${viewMode === 'compare' ? 'bg-slate-700 shadow text-white' : 'text-slate-400'}`}
               >
                 Compare
               </button>
               <button
                 onClick={() => setViewMode('processed')}
                 className={`px-2.5 py-1 text-xs font-medium rounded transition-all ${viewMode === 'processed' ? 'bg-slate-700 shadow text-white' : 'text-slate-400'}`}
               >
                 Result
               </button>
            </div>
          </div>
       </div>

       {/* Mobile Bottom Action Bar */}
       <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 px-3 py-3 z-50 flex items-center justify-between gap-2">
          <button
            onClick={() => setIsWatermarking(!isWatermarking)}
            className={`p-2.5 rounded-lg transition-all ${isWatermarking ? 'bg-teal-900/30 text-teal-500 border border-teal-800' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}
          >
            <Stamp size={20} />
          </button>

          {/* Edit Floor Plan - Only for vector mode */}
          {mode === 'vector' && (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="p-2.5 rounded-lg transition-all bg-amber-600 text-white"
                title="Edit floor plan"
              >
                <Pencil size={20} />
              </button>
              
              {/* Enhanced Look Toggle (beta) - Mobile */}
              <button
                onClick={() => setEnhancedLook(!enhancedLook)}
                className={`p-2.5 rounded-lg transition-all ${
                  enhancedLook 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-slate-800 text-slate-400 border border-slate-700'
                }`}
                title="Enhanced look (beta)"
              >
                <Sparkles size={20} />
              </button>
            </>
          )}

          {/* Amend Plan - Mobile */}
          {onRequestAmendment && (
            <button
              onClick={() => { setShowAmendPanel(!showAmendPanel); setShowAiSidebar(false); }}
              className={`p-2.5 rounded-lg transition-all ${showAmendPanel ? 'bg-orange-600 text-white' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}
              title="Amend plan"
            >
              <MessageSquarePlus size={20} />
            </button>
          )}

          {/* AI Editor Toggle - For visual mode */}
          {mode === 'visual' ? (
            <button
              onClick={() => { setShowAiSidebar(!showAiSidebar); setShowAmendPanel(false); }}
              className={`flex-1 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 text-sm ${showAiSidebar ? 'bg-teal-600 text-white' : 'bg-slate-700 text-white'}`}
            >
              <Wand2 size={16} />
              {showAiSidebar ? 'Close' : 'AI Editor'}
            </button>
          ) : (
            <button
              onClick={onAccept}
              className="flex-1 bg-slate-700 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
            >
              <Layers size={16} />
              Continue Editing
            </button>
          )}

          <div className="relative" ref={downloadMenuRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              className="bg-teal-600 text-white py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm"
            >
              <Download size={16} />
              Download
            </button>
            
            {showDownloadMenu && (
              <div className="absolute right-0 bottom-full mb-2 w-48 bg-slate-900 rounded-lg shadow-xl border border-slate-700 py-2 z-50">
                 <button 
                   type="button"
                   onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('jpeg'); }}
                   className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer"
                 >
                   JPEG (Smaller)
                 </button>
                 <button 
                   type="button"
                   onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('png'); }}
                   className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer"
                 >
                   PNG (Lossless)
                 </button>
              </div>
            )}
          </div>
       </div>
       

       {/* Watermark Controls Panel - Desktop */}
       {isWatermarking && (
          <div className="hidden sm:flex bg-slate-900 border-b border-slate-800 px-6 py-3 items-center gap-6 animate-slide-down">
              <input 
                type="file" 
                ref={logoInputRef}
                accept="image/png, image/jpeg, image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              
              {!watermark ? (
                 <button 
                   onClick={() => logoInputRef.current?.click()}
                   className="flex items-center gap-2 px-4 py-2 bg-teal-900/30 text-teal-400 rounded-lg hover:bg-teal-900/50 transition-colors text-sm font-medium border border-teal-800/50"
                 >
                   <Upload size={16} />
                   Upload Logo
                 </button>
              ) : (
                <div className="flex items-center gap-6 w-full">
                  <div className="flex items-center gap-2">
                     <img src={watermark.imageUrl} alt="Logo" className="w-8 h-8 object-contain rounded bg-slate-800 border border-slate-700" />
                     <button 
                       onClick={() => setWatermark(null)}
                       className="text-red-400 hover:bg-red-900/30 p-1.5 rounded-md transition-colors"
                     >
                       <Trash2 size={16} />
                     </button>
                  </div>

                  <div className="h-8 w-px bg-slate-800"></div>

                  <div className="flex items-center gap-3">
                     <span className="text-xs font-medium text-slate-400">Opacity</span>
                     <input 
                       type="range" 
                       min="10" 
                       max="100" 
                       value={watermark.opacity * 100}
                       onChange={(e) => setWatermark({...watermark, opacity: parseInt(e.target.value) / 100})}
                       className="w-32 accent-orange-500"
                     />
                     <span className="text-xs w-8 text-right text-slate-300">{Math.round(watermark.opacity * 100)}%</span>
                  </div>

                  <div className="flex items-center gap-3">
                     <span className="text-xs font-medium text-slate-400">Size</span>
                     <input 
                       type="range" 
                       min="5" 
                       max="80" 
                       value={watermark.scale * 100}
                       onChange={(e) => setWatermark({...watermark, scale: parseInt(e.target.value) / 100})}
                       className="w-32 accent-orange-500"
                     />
                     <span className="text-xs w-8 text-right text-slate-300">{Math.round(watermark.scale * 100)}%</span>
                  </div>

                  <div className="ml-auto text-xs text-teal-400 bg-teal-900/20 px-3 py-1.5 rounded-full border border-orange-900/50 flex items-center gap-1.5">
                    <Move size={12} />
                    Drag logo on image to position
                  </div>
                </div>
              )}
          </div>
       )}

       {/* Watermark Controls Panel - Mobile */}
       {isWatermarking && (
          <div className="sm:hidden bg-slate-900 border-b border-slate-800 px-3 py-2 animate-slide-down">
              <input 
                type="file" 
                ref={logoInputRef}
                accept="image/png, image/jpeg, image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              
              {!watermark ? (
                 <button 
                   onClick={() => logoInputRef.current?.click()}
                   className="flex items-center gap-2 px-3 py-2 bg-teal-900/30 text-teal-400 rounded-lg text-sm font-medium border border-teal-800/50 w-full justify-center"
                 >
                   <Upload size={16} />
                   Upload Logo
                 </button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                       <img src={watermark.imageUrl} alt="Logo" className="w-8 h-8 object-contain rounded bg-slate-800 border border-slate-700" />
                       <span className="text-xs text-slate-400">Drag on image to move</span>
                    </div>
                    <button 
                      onClick={() => setWatermark(null)}
                      className="text-red-400 bg-red-900/20 p-1.5 rounded-md"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1 flex items-center gap-2">
                       <span className="text-[10px] font-medium text-slate-500">Opacity</span>
                       <input 
                         type="range" 
                         min="10" 
                         max="100" 
                         value={watermark.opacity * 100}
                         onChange={(e) => setWatermark({...watermark, opacity: parseInt(e.target.value) / 100})}
                         className="flex-1 accent-orange-500"
                       />
                    </div>

                    <div className="flex-1 flex items-center gap-2">
                       <span className="text-[10px] font-medium text-slate-500">Size</span>
                       <input 
                         type="range" 
                         min="5" 
                         max="80" 
                         value={watermark.scale * 100}
                         onChange={(e) => setWatermark({...watermark, scale: parseInt(e.target.value) / 100})}
                         className="flex-1 accent-orange-500"
                       />
                    </div>
                  </div>
                </div>
              )}
          </div>
       )}

       {/* Amendment Panel - Desktop */}
       {showAmendPanel && onRequestAmendment && (
         <div className="hidden sm:block bg-slate-900 border-b border-slate-800 px-6 py-4 animate-slide-down">
           <div className="max-w-3xl mx-auto space-y-3">
             {/* Header row */}
             <div className="flex items-center justify-between">
               <div className="flex items-center gap-2">
                 <MessageSquarePlus size={16} className="text-orange-400" />
                 <span className="text-sm font-semibold text-white">Request AI Amendments</span>
                 <span className="text-[10px] bg-orange-900/40 text-orange-300 border border-orange-800/50 px-2 py-0.5 rounded-full">10 credits</span>
               </div>
               <button onClick={() => setShowAmendPanel(false)} className="text-slate-500 hover:text-slate-300 transition-colors p-1 rounded">
                 <X size={16} />
               </button>
             </div>

             {/* Suggestion chips */}
             <div className="flex flex-wrap gap-2">
               {AMENDMENT_SUGGESTIONS.map(suggestion => (
                 <button
                   key={suggestion}
                   onClick={() => setAmendmentText(prev => prev ? `${prev.trimEnd()}\n${suggestion}` : suggestion)}
                   className="text-xs px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:bg-orange-900/30 hover:text-orange-300 hover:border-orange-700 transition-all"
                 >
                   + {suggestion}
                 </button>
               ))}
             </div>

             {/* Textarea + submit */}
             <div className="flex gap-3">
               <textarea
                 value={amendmentText}
                 onChange={e => setAmendmentText(e.target.value)}
                 placeholder="Describe the changes you want — e.g. 'Add a door to the master bedroom and remove the wall between the kitchen and dining area'"
                 rows={3}
                 className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-orange-600 resize-none"
               />
               <div className="flex flex-col gap-2 justify-end">
                 <button
                   onClick={async () => {
                     if (!amendmentText.trim()) return;
                     setIsSubmittingAmendment(true);
                     try {
                       await onRequestAmendment(amendmentText);
                       setAmendmentText('');
                       setShowAmendPanel(false);
                     } finally {
                       setIsSubmittingAmendment(false);
                     }
                   }}
                   disabled={!amendmentText.trim() || isSubmittingAmendment}
                   className="px-5 py-2 rounded-lg font-medium text-sm bg-orange-600 hover:bg-orange-500 text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                 >
                   {isSubmittingAmendment ? (
                     <>Processing…</>
                   ) : (
                     <><Sparkles size={15} /> Apply Amendments</>
                   )}
                 </button>
                 <p className="text-[10px] text-slate-500 text-center">Gemini re-generates<br />your floor plan</p>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Amendment Panel - Mobile */}
       {showAmendPanel && onRequestAmendment && (
         <div className="sm:hidden bg-slate-900 border-b border-slate-800 px-3 py-3 animate-slide-down space-y-3">
           <div className="flex items-center justify-between">
             <div className="flex items-center gap-2">
               <MessageSquarePlus size={15} className="text-orange-400" />
               <span className="text-sm font-semibold text-white">Amend Plan</span>
               <span className="text-[10px] bg-orange-900/40 text-orange-300 border border-orange-800/50 px-2 py-0.5 rounded-full">10 credits</span>
             </div>
             <button onClick={() => setShowAmendPanel(false)} className="text-slate-500 p-1">
               <X size={16} />
             </button>
           </div>

           {/* Chips – horizontal scroll */}
           <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
             {AMENDMENT_SUGGESTIONS.map(suggestion => (
               <button
                 key={suggestion}
                 onClick={() => setAmendmentText(prev => prev ? `${prev.trimEnd()}\n${suggestion}` : suggestion)}
                 className="flex-shrink-0 text-xs px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:bg-orange-900/30 hover:text-orange-300 hover:border-orange-700 transition-all"
               >
                 + {suggestion}
               </button>
             ))}
           </div>

           <textarea
             value={amendmentText}
             onChange={e => setAmendmentText(e.target.value)}
             placeholder="Describe the changes you want…"
             rows={3}
             className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-orange-600 resize-none"
           />
           <button
             onClick={async () => {
               if (!amendmentText.trim()) return;
               setIsSubmittingAmendment(true);
               try {
                 await onRequestAmendment(amendmentText);
                 setAmendmentText('');
                 setShowAmendPanel(false);
               } finally {
                 setIsSubmittingAmendment(false);
               }
             }}
             disabled={!amendmentText.trim() || isSubmittingAmendment}
             className="w-full py-2.5 rounded-lg font-medium text-sm bg-orange-600 hover:bg-orange-500 text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
           >
             {isSubmittingAmendment ? 'Processing…' : <><Sparkles size={15} /> Apply Amendments (10 credits)</>}
           </button>
         </div>
       )}

       {/* Main Content Area with optional AI Sidebar */}
       <div className="flex-1 flex overflow-hidden">
         {/* View Area */}
         <div className={`flex-1 overflow-hidden p-4 sm:p-8 pb-20 sm:pb-8 flex items-center justify-center bg-black transition-all duration-300 ${showAiSidebar ? 'mr-0' : ''}`}>
          
          {/* Floor Plan Editor Mode */}
          {isEditing ? (
            <div className="w-full h-full flex flex-col min-h-[500px]">
              {/* Editor Header */}
              <div className="flex items-center justify-between bg-slate-800 px-4 py-2 rounded-t-lg border-b border-slate-700">
                <h3 className="text-sm font-medium text-amber-400 flex items-center gap-2">
                  <Pencil size={16} />
                  Editing Floor Plan
                </h3>
                <button
                  onClick={() => setIsEditing(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                  title="Exit Editor"
                >
                  <X size={18} />
                </button>
              </div>
              
              {/* Floor Plan Editor Component */}
              <FloorPlanEditor
                imageUrl={mode === 'vector' ? originalUrl : editorImageUrl}
                initialData={{ measurements }}
                onSave={handleEditorSave}
                width={Math.min(mode === 'vector' && vectorData ? vectorData.width : 1200, window.innerWidth - 64, 1400)}
                height={Math.min(mode === 'vector' && vectorData ? vectorData.height : 800, window.innerHeight - 200, 900)}
                className="flex-1 min-h-[400px]"
                enhancedLook={enhancedLook}
                userCredits={userCredits}
                downloadCreditCost={downloadCreditCost}
                onConsumeCredits={onConsumeCredits}
                onOrderPro={onOrderPro}
                isLoggedIn={isLoggedIn}
                brandingProfile={brandingProfile}
              />
            </div>
          ) : showAiSidebar && mode === 'visual' ? (
          /* AI Editor Mode - Full width interactive canvas */
          <ImageCanvas
            imageUrl={currentImageUrl}
            tool={aiTool}
            selection={aiSelection}
            onSelectionChange={setAiSelection}
            disabled={isAiProcessing}
            onUndo={handleUndo}
            onRedo={handleRedo}
            className="w-full h-full"
          />
          ) : (
          <div className="relative shadow-2xl rounded-lg overflow-hidden bg-slate-900 ring-1 ring-white/10 w-fit h-fit flex justify-center">
            
            {viewMode === 'processed' && (
              <div className="relative group">
                <img 
                  ref={processedImageRef}
                  src={processedUrl} 
                  alt="Processed" 
                  className="max-w-full max-h-[80vh] object-contain block" 
                />
                {watermark && (
                  <>
                    <div
                      className={`absolute select-none origin-center ${
                        isDraggingLogo 
                          ? 'cursor-grabbing z-50' 
                          : 'cursor-move z-40 hover:z-50'
                      }`}
                      style={{
                        left: `${watermark.x * 100}%`,
                        top: `${watermark.y * 100}%`,
                        width: `${watermark.scale * 100}%`,
                        transform: 'translate(0, 0)',
                        touchAction: 'none'
                      }}
                      onMouseDown={startDrag}
                      onTouchStart={startDrag}
                    >
                         {/* Visual wrapper for the image */}
                         <div className={`
                             w-full h-full relative transition-all duration-200
                             ${isDraggingLogo 
                               ? 'ring-2 ring-orange-500/80 shadow-[0_0_20px_rgba(249,115,22,0.3)] bg-teal-500/5 backdrop-blur-[1px] rounded-sm' 
                               : 'hover:ring-1 hover:ring-white/40 rounded-sm'
                             }
                         `}>
                             <img 
                               src={watermark.imageUrl}
                               alt="Watermark"
                               className="w-full h-full object-contain pointer-events-none"
                               style={{ opacity: watermark.opacity }}
                             />
                             
                             {/* Corner markers for drag indication */}
                             {isDraggingLogo && (
                                <>
                                    <div className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-teal-500 border border-white rounded-full shadow-sm"></div>
                                    <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-teal-500 border border-white rounded-full shadow-sm"></div>
                                    <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-teal-500 border border-white rounded-full shadow-sm"></div>
                                    <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-teal-500 border border-white rounded-full shadow-sm"></div>
                                </>
                             )}
                         </div>
                    </div>

                    {/* Helper Tooltip when dragging */}
                    {isDraggingLogo && (
                        <div 
                            className="absolute pointer-events-none z-50 bg-slate-900/95 text-teal-200 text-[10px] font-medium px-2.5 py-1.5 rounded-md shadow-xl backdrop-blur-md border border-teal-500/30 flex items-center gap-2 transform -translate-x-1/2 -translate-y-full mt-[-8px]"
                            style={{
                                left: `${(watermark.x + watermark.scale/2) * 100}%`,
                                top: `${watermark.y * 100}%`,
                            }}
                        >
                            <Move size={10} className="text-teal-400" />
                            <span>
                                X: {Math.round(watermark.x * 100)}% Y: {Math.round(watermark.y * 100)}%
                            </span>
                        </div>
                    )}

                    {/* Hover Hint when not dragging */}
                    {!isDraggingLogo && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none flex items-center gap-1.5 border border-white/10 z-30">
                            <Move size={12} />
                            Drag watermark to reposition
                        </div>
                    )}
                  </>
                )}
              </div>
            )}

            {viewMode === 'original' && (
               <img src={originalUrl} alt="Original" className="max-w-full max-h-[80vh] object-contain block" />
            )}

            {viewMode === 'compare' && (
              <div ref={containerRef} className="relative select-none group w-fit h-fit">
                 <img 
                    src={processedUrl} 
                    alt="After" 
                    className="max-w-full max-h-[80vh] object-contain block select-none pointer-events-none" 
                    draggable={false}
                 />

                 {/* Top Image: Original - Clipped */}
                 <img 
                    src={originalUrl} 
                    alt="Before" 
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none" 
                    style={{ 
                      clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` 
                    }}
                 />

                 {/* Divider Line */}
                 <div 
                    className="absolute inset-y-0 w-0.5 bg-white shadow-[0_0_15px_rgba(0,0,0,0.5)] cursor-ew-resize z-20 pointer-events-none"
                    style={{ left: `${sliderPosition}%` }}
                 >
                    {/* Handle Button */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-400">
                      <ChevronLeft size={14} className="text-slate-400" />
                      <GripVertical size={14} className="text-slate-300 mx-[-2px]" />
                      <ChevronRight size={14} className="text-slate-400" />
                    </div>
                 </div>

                 {/* Interactive Input Overlay */}
                 <input 
                   type="range"
                   min="0"
                   max="100"
                   value={sliderPosition}
                   onChange={handleSliderChange}
                   className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-30 touch-none"
                   aria-label="Comparison slider"
                 />
                 
                 {/* Floating Labels */}
                 <div className="absolute top-4 left-4 bg-black/60 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-md shadow-sm border border-white/10 pointer-events-none transition-opacity duration-300" style={{ opacity: sliderPosition < 10 ? 0 : 1 }}>
                    Original
                 </div>
                 <div className="absolute top-4 right-4 bg-teal-600/80 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-md shadow-sm border border-white/10 pointer-events-none transition-opacity duration-300" style={{ opacity: sliderPosition > 90 ? 0 : 1 }}>
                    Converted
                 </div>
              </div>
            )}
          </div>
          )}
         </div>

         {/* AI Editor Sidebar - Slides in from right */}
         {showAiSidebar && mode === 'visual' && (
           <EditorRightSidebar
             activeTool={aiTool}
             onToolChange={setAiTool}
             hasSelection={aiSelection !== null}
             onAmendmentClick={handleAiAmendmentClick}
             onCustomPromptSubmit={handleAiCustomPrompt}
             onUpdatePlan={handleAiUpdatePlan}
             onDownload={handleAiDownload}
             onAddText={handleAddText}
             onUndo={handleUndo}
             onRedo={handleRedo}
             canUndo={canUndo}
             canRedo={canRedo}
             isProcessing={isAiProcessing}
             className="hidden sm:flex animate-in slide-in-from-right duration-300"
           />
         )}
       </div>
       
       {/* 360 Viewer Modal */}
       {show360Viewer && (
         <Viewer360 
           imageUrl={viewer360Mode === 'original' ? originalUrl : processedUrl}
           onClose={() => setShow360Viewer(false)}
           title={viewer360Mode === 'original' ? '360° Original' : '360° Result'}
         />
       )}
    </div>
  );
};

export default ResultViewer;
